import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

/** Cores por bloco de financiamento - criadas dinamicamente */
const CORES_BLOCOS_BASE = [
  '#27AE60', '#2D7D9A', '#8E44AD', '#E67E22', '#1E3A5F',
  '#E74C3C', '#F39C12', '#16A085', '#C0392B', '#2980B9',
];

/** Normalizar mes para exibicao: aceita "2024/01", "2024-01", 202401 */
const formatMes = (mes: number | string): string => {
  const s = String(mes);
  if (s.length === 7 && (s.includes('/') || s.includes('-'))) {
    return s.replace('/', '-');
  }
  if (s.length === 6) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
  }
  return s;
};

const MonitoramentoRepasses: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getRepasses(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de repasses FNS.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  // --- Dados derivados ---
  // kpis e ARRAY (nao objeto!)
  const kpisArray: any[] = useMemo(() => Array.isArray(data?.kpis) ? data.kpis : [], [data]);
  const evolucaoRaw: any[] = data?.evolucao || [];

  // KPIs calculados: total_recebido = sum(valor_total de cada bloco)
  const totalRecebido = useMemo(
    () => kpisArray.reduce((acc, kpi) => acc + Number(kpi.valor_total ?? 0), 0),
    [kpisArray],
  );

  // Competencias = meses distintos na evolucao
  const mesesDistintos = useMemo(() => {
    const set = new Set<string>();
    for (const item of evolucaoRaw) {
      set.add(formatMes(item.mes));
    }
    return Array.from(set).sort();
  }, [evolucaoRaw]);

  // Blocos dinamicos: extrair do dataset
  const blocos = useMemo(() => {
    const set = new Set<string>();
    for (const kpi of kpisArray) {
      if (kpi.bloco) set.add(String(kpi.bloco));
    }
    for (const item of evolucaoRaw) {
      if (item.bloco) set.add(String(item.bloco));
    }
    return Array.from(set);
  }, [kpisArray, evolucaoRaw]);

  // Mapa de cores por bloco
  const coresBlocos = useMemo(() => {
    const map: Record<string, string> = {};
    blocos.forEach((bloco, idx) => {
      map[bloco] = CORES_BLOCOS_BASE[idx % CORES_BLOCOS_BASE.length];
    });
    return map;
  }, [blocos]);

  // Pivotar evolucao flat {mes, bloco, valor_total, qtd_total}
  // -> {mes: "2026-03", Outros: 310143.60, ...}
  const porBlocoCompetencia = useMemo(() => {
    const map = new Map<string, Record<string, any>>();
    for (const item of evolucaoRaw) {
      const mesFormatado = formatMes(item.mes);
      if (!map.has(mesFormatado)) {
        map.set(mesFormatado, { competencia: mesFormatado });
      }
      const row = map.get(mesFormatado)!;
      const bloco = String(item.bloco ?? 'Outros');
      row[bloco] = (row[bloco] ?? 0) + Number(item.valor_total ?? 0);
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.competencia).localeCompare(String(b.competencia))
    );
  }, [evolucaoRaw]);

  // Evolucao mensal total: agrupar evolucao por mes, sum(valor_total)
  const evolucaoMensal = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of evolucaoRaw) {
      const mesFormatado = formatMes(item.mes);
      map.set(mesFormatado, (map.get(mesFormatado) ?? 0) + Number(item.valor_total ?? 0));
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mes, total]) => ({
        competencia: mes,
        total: Math.round(total * 100) / 100,
      }));
  }, [evolucaoRaw]);

  // Variacao: comparar primeiro e ultimo mes
  const variacaoPct = useMemo(() => {
    if (evolucaoMensal.length < 2) return 0;
    const primeiro = evolucaoMensal[0].total;
    const ultimo = evolucaoMensal[evolucaoMensal.length - 1].total;
    if (primeiro === 0) return 0;
    return ((ultimo - primeiro) / primeiro) * 100;
  }, [evolucaoMensal]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de repasses FNS.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Repasses FNS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="body1" color="text.secondary">
              {municipioAtivo.nome}
            </Typography>
            {data?.fonteReal && (
              <Chip
                label="Dados Oficiais - Portal da Transparencia"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
            {data && !data.fonteReal && (
              <Chip
                label="Estimativa via producao BPA"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Total Recebido"
            valor={`R$ ${totalRecebido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            icone={<AccountBalanceWalletIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Variacao vs Periodo Anterior"
            valor={`${variacaoPct > 0 ? '+' : ''}${variacaoPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
            icone={<TrendingUpIcon />}
            cor={variacaoPct >= 0 ? '#27AE60' : '#E74C3C'}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Competencias no Periodo"
            valor={mesesDistintos.length.toLocaleString('pt-BR')}
            icone={<CalendarMonthIcon />}
            cor="#2D7D9A"
          />
        </Grid>
      </Grid>

      {/* Por bloco por competencia - BarChart empilhado */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Repasses por Bloco de Financiamento
        </Typography>
        {porBlocoCompetencia.length > 0 ? (
          <>
            {/* Legenda customizada para cores dos blocos */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              {blocos.map((bloco) => (
                <Box key={bloco} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: coresBlocos[bloco] }} />
                  <Typography variant="caption" fontWeight={500}>{bloco}</Typography>
                </Box>
              ))}
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={porBlocoCompetencia} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="competencia" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value: any, name: any) => [
                    `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                    name,
                  ]}
                />
                <Legend />
                {blocos.map((bloco) => (
                  <Bar
                    key={bloco}
                    dataKey={bloco}
                    stackId="repasses"
                    fill={coresBlocos[bloco]}
                    name={bloco}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de repasses por bloco para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Evolucao mensal total - LineChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Evolucao Mensal dos Repasses
        </Typography>
        {evolucaoMensal.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={evolucaoMensal} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="competencia" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: number) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value: any) => [
                  `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  'Total',
                ]}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#1E3A5F"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de evolucao mensal para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Tabela resumo por bloco */}
      {kpisArray.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Resumo por Bloco
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Bloco</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Total (R$)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Componentes</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Periodos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>% Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kpisArray.map((kpi: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: coresBlocos[kpi.bloco] || '#999',
                          }}
                        />
                        {kpi.bloco}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {Number(kpi.valor_total ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="right">
                      {Number(kpi.componentes ?? 0).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell align="right">
                      {Number(kpi.periodos ?? 0).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell align="right">
                      {Number(kpi.pct_total ?? 0).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'rgba(30, 58, 95, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {totalRecebido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>-</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {Math.max(...kpisArray.map((k: any) => Number(k.periodos ?? 0)))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="repasses"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoRepasses;
